import React, { useState, useEffect } from 'react';
import { Play, Video, Mic, Pencil, Check, Download, BarChart2, Send, X } from 'lucide-react';
import { RecordingService, Recording } from '../services/RecordingService';
import { useUser } from '../context/UserContext';
import { supabase } from '../lib/supabase';
import { PlaybackModal } from './RecordingsList';
import { VocalBlueprintModal } from './VocalBlueprintModal';
import { AudioWaveform } from './AudioWaveform';

interface TournamentEntriesListProps {
  tournamentId: string;
}

export const TournamentEntriesList: React.FC<TournamentEntriesListProps> = ({ tournamentId }) => {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRecording, setSelectedRecording] = useState<Recording | null>(null);
  const [playingAudio, setPlayingAudio] = useState<Recording | null>(null);
  const [playingVideo, setPlayingVideo] = useState<Recording | null>(null);
  const [showVocalBlueprintModal, setShowVocalBlueprintModal] = useState(false);
  const [blueprintData, setBlueprintData] = useState<string | null>(null);
  const [blueprintLoading, setBlueprintLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [isDownloading, setIsDownloading] = useState<string | null>(null);

  const { user } = useUser();
  const recordingService = new RecordingService();

  useEffect(() => {
    loadTournamentEntries();
  }, [tournamentId]);

  const loadTournamentEntries = async () => {
    try {
      setLoading(true);
      if (!user?.id) {
        setRecordings([]);
        setLoading(false);
        return;
      }
      const data = await recordingService.getTournamentEntries(tournamentId, user.id);
      setRecordings(data);
    } catch (error) {
      console.error('Error loading tournament entries:', error);
      setError('Failed to load tournament entries');
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = (recording: Recording) => {
    if (recording.recording_type === 'video') {
      setPlayingAudio(null);
      setPlayingVideo(recording);
    } else {
      setPlayingVideo(null);
      setPlayingAudio(recording);
    }
  };

  const handleEditTitle = async (recording: Recording) => {
    setEditingId(recording.id);
    setEditingTitle(recording.title);
  };

  const handleSaveTitle = async (recording: Recording) => {
    try {
      await recordingService.updateRecordingTitle(recording.id, editingTitle);
      setRecordings(recordings.map(r =>
        r.id === recording.id ? { ...r, title: editingTitle } : r
      ));
      setEditingId(null);
    } catch (error) {
      console.error('Error updating title:', error);
      setError('Failed to update recording title');
    }
  };

  const handleDownload = async (recording: Recording) => {
    try {
      setIsDownloading(recording.id);
      setError(null);

      if (!recording.song?.audio_url) {
        throw new Error('No backing track available for this recording');
      }

      await recordingService.downloadRecordingWithBackingTrack(recording);
    } catch (error) {
      console.error('Error downloading recording:', error);
      setError(error instanceof Error ? error.message : 'Failed to download recording');
    } finally {
      setIsDownloading(null);
    }
  };


  const handleViewScorecard = async (recording: Recording) => {
    setSelectedRecording(recording);
    setBlueprintData(null);
    setBlueprintLoading(true);
    setShowVocalBlueprintModal(true);

    try {
      const { data: performance } = await supabase
        .from('performances')
        .select('id')
        .eq('recording_id', recording.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!performance) {
        setBlueprintLoading(false);
        return;
      }

      const { data: analysis } = await supabase
        .from('performance_analysis')
        .select('report_compressed_b64')
        .eq('performance_id', performance.id)
        .maybeSingle();

      if (analysis?.report_compressed_b64) {
        setBlueprintData(analysis.report_compressed_b64);
      }
    } catch (err) {
      console.error('Error loading vocal blueprint:', err);
    } finally {
      setBlueprintLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-center">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (recordings.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-orange-500/20 to-blue-500/20 mb-4">
          <Mic className="w-8 h-8 text-orange-400" />
        </div>
        <h3 className="text-xl font-semibold mb-2">No Tournament Entries Yet</h3>
        <p className="text-white/60">
          Record your performance for this tournament to submit your entry
        </p>
      </div>
    );
  }

  const submittedEntry = recordings.find(r => r.is_tournament_submission);

  return (
    <div className="space-y-4">
      {submittedEntry && (
        <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Send className="w-5 h-5 text-green-400" />
            <h4 className="font-semibold text-green-400">Submitted Entry</h4>
          </div>
          <p className="text-sm text-white/70">
            You have submitted your entry for this tournament. You can still record practice versions, but only one entry can be submitted.
          </p>
        </div>
      )}

      <div className="grid gap-4">
        {recordings.map((recording) => (
          <div
            key={recording.id}
            className={`bg-kod-purple-800/30 backdrop-blur-sm border rounded-lg p-4 hover:bg-kod-purple-700/40 transition-all group ${
              recording.is_tournament_submission ? 'border-green-500/50 shadow-lg shadow-green-500/20' : 'border-kod-purple-600/50'
            }`}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  recording.recording_type === 'video'
                    ? 'bg-gradient-to-br from-blue-500/20 to-teal-500/20'
                    : 'bg-gradient-to-br from-orange-500/20 to-blue-500/20'
                }`}>
                  {recording.recording_type === 'video' ? (
                    <Video className="w-6 h-6 text-blue-400" />
                  ) : (
                    <Mic className="w-6 h-6 text-orange-400" />
                  )}
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex-1 min-w-0">
                    {editingId === recording.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          className="flex-1 bg-kod-purple-900/50 border border-kod-purple-600 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                          autoFocus
                        />
                        <button
                          onClick={() => handleSaveTitle(recording)}
                          className="p-1 hover:bg-kod-purple-700 rounded transition-colors"
                        >
                          <Check className="w-4 h-4 text-green-400" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold truncate">{recording.title}</h3>
                        <button
                          onClick={() => handleEditTitle(recording)}
                          className="p-1 opacity-0 group-hover:opacity-100 hover:bg-kod-purple-700 rounded transition-all"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <p className="text-sm text-white/60">
                      {recording.song?.title} - {recording.song?.artist}
                    </p>
                    <p className="text-xs text-white/40 mt-1">
                      {formatDate(recording.created_at)}
                      {recording.tournament_round !== null && recording.tournament_round !== undefined && (
                        <span className="ml-2 px-1.5 py-0.5 rounded bg-white/10 text-white/60">
                          Round {recording.tournament_round}
                        </span>
                      )}
                    </p>
                    {recording.is_tournament_submission && recording.submitted_at && (
                      <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                        <Send className="w-3 h-3" />
                        Submitted {formatDate(recording.submitted_at)}
                      </p>
                    )}
                  </div>

                  {recording.is_tournament_submission && (
                    <div className="px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-full text-xs font-semibold text-green-400">
                      Submitted
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => handlePlay(recording)}
                    className="px-3 py-1.5 bg-gradient-to-r from-orange-500 to-blue-500 rounded-lg text-sm font-semibold hover:shadow-lg transition-all flex items-center gap-2"
                  >
                    <Play className="w-4 h-4" />
                    Play
                  </button>

                  <button
                    onClick={() => handleViewScorecard(recording)}
                    className="px-3 py-1.5 bg-kod-purple-700/50 hover:bg-kod-purple-600/50 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
                  >
                    <BarChart2 className="w-4 h-4" />
                    Vocal Blueprint
                  </button>


                  <button
                    onClick={() => handleDownload(recording)}
                    disabled={isDownloading === recording.id}
                    className="px-3 py-1.5 bg-kod-purple-700/50 hover:bg-kod-purple-600/50 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    <Download className="w-4 h-4" />
                    {isDownloading === recording.id ? 'Downloading...' : 'Download'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {playingAudio && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 w-full max-w-2xl px-4 z-50">
          <div className="bg-kod-purple-800 rounded-lg p-4 shadow-lg border border-kod-purple-600">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium">{playingAudio.title}</h3>
                <p className="text-sm text-white/70">
                  {playingAudio.song?.title} - {playingAudio.song?.artist}
                </p>
              </div>
              <button
                onClick={() => setPlayingAudio(null)}
                className="p-2 hover:bg-kod-purple-700 rounded-full"
              >
                <X size={20} />
              </button>
            </div>
            <AudioWaveform url={playingAudio.recording_url} />
          </div>
        </div>
      )}

      {playingVideo && (
        <PlaybackModal
          recording={playingVideo}
          onClose={() => setPlayingVideo(null)}
          onSyncCalculated={(offset) => {
            setRecordings(prev => prev.map(r =>
              r.id === playingVideo.id ? { ...r, audio_sync_offset: offset } : r
            ));
          }}
        />
      )}

      {showVocalBlueprintModal && selectedRecording && (
        <VocalBlueprintModal
          isOpen={showVocalBlueprintModal}
          compressedData={blueprintData}
          title={`Vocal Blueprint: ${selectedRecording.song?.title || selectedRecording.title}`}
          vocalRecordingUrl={selectedRecording.vocals_only_url || selectedRecording.recording_url}
          leadVocalUrl={selectedRecording.song?.lead_vocal_url}
          finalRecordingUrl={selectedRecording.mixed_recording_url || undefined}
          onClose={() => {
            setShowVocalBlueprintModal(false);
            setSelectedRecording(null);
            setBlueprintData(null);
          }}
        />
      )}
    </div>
  );
};
